import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";

const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response;
};

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
  return Promise.reject(error);
};

// Base URLs come from the environment (see .env.example). CRA inlines
// REACT_APP_* at build time, so a Docker/CI build must pass them as build args
// — changing them later needs a rebuild, not just a restart.
const requireEnv = (name: string, value: string | undefined): string => {
  if (!value) {
    throw new Error(
      `Missing ${name}. Copy .env.example to .env and set the API host.`
    );
  }
  return value.replace(/\/+$/, "");
};

const baseURL = requireEnv(
  "REACT_APP_API_BASE_URL",
  process.env.REACT_APP_API_BASE_URL
);

// Define the Axios instance
const axiosClient: AxiosInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  },
});

export const updateAuthorizationHeader = (token: string) => {
  axiosClient.defaults.headers["Authorization"] = `Bearer ${token}`;
  localStorage.setItem("token", token);
};

/** The session token this tab is currently authenticating with ("" if none). */
export const getStoredToken = (): string => localStorage.getItem("token") || "";

/**
 * Drop the session entirely. Note this removes the key rather than storing an
 * empty string, so `localStorage.getItem("token")` is null (not "") afterwards.
 */
export const clearAuthorizationHeader = () => {
  delete axiosClient.defaults.headers["Authorization"];
  localStorage.removeItem("token");
};

/**
 * True when a failed response came from a request that was signed with a token we
 * have since replaced — i.e. it belongs to a session that no longer exists. Such a
 * failure must not tear down the *current* session: during the Xperience deep-link
 * handoff a request made with the previous token can land after the new one is
 * installed, and treating that as "you are logged out" kills a live exam handoff.
 */
export const isStaleAuthFailure = (config: any): boolean => {
  const sentWith = String(config?.headers?.Authorization ?? "");
  if (!sentWith) return false;
  return sentWith !== `Bearer ${getStoredToken()}`;
};

export const loginClient: AxiosInstance = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

// RedTeam Xperience (Laravel admin) v3 API — the identity source for the login
// form: it owns the student records, sends the OTP and mints the portal SSO JWT.
const experienceBaseURL = requireEnv(
  "REACT_APP_EXPERIENCE_BASE_URL",
  process.env.REACT_APP_EXPERIENCE_BASE_URL
);

export const experienceClient: AxiosInstance = axios.create({
  baseURL: experienceBaseURL,
  headers: { "Content-Type": "application/json" },
});

axiosClient.interceptors.request.use(onRequest, onRequestError);
axiosClient.interceptors.response.use(onResponse, onResponseError);

export default axiosClient;
